import { useMemo } from 'react';
import { toBookCategories } from 'src/mapper/BookCategory.mapper';
import { toFields } from 'src/mapper/Field.mapper';
import { toLanguages } from 'src/mapper/Language.mapper';
import { toBookPublic, toBookPublics } from 'src/mapper/Public.mapper';
import { IResponse } from 'src/types/DTO/IResponse';
import { IBookShelf } from 'src/types/DTO/bookStorage/IBookShelf';
import { IField } from 'src/types/DTO/field/IField';
import { ILanguage } from 'src/types/DTO/language/ILanguage';
import { IBookDetailPublic } from 'src/types/DTO/public/IBookDetailPublic';
import { IResponseSingle } from 'src/types/DTO/reponse/IReponseSingle';
import { IParamBasic } from 'src/types/ParamBasic';
import { axiosAI, endpoints } from 'src/utils/axios';
import useSWR from 'swr';
// import OpenAI from "openai";
import {
  AI_GEMINI_HOST,
  GEMINI_FLAG,
  // GROK_FLAG,
  // AI_GROK_HOST,
  // GROK_API_KEY
} from 'src/config-global';
import axiosPublic, { fetcherPublic } from './base';
// ----------------------------------------------------------------------

export async function findBooks(
  params?: IParamBasic & {
    languageIds?: number[];
    fieldIds?: number[];
    categoryIds?: number[];
    ids?: number[] | string[];
  }
) {
  const URL = endpoints.public.find_book;

  const { data } = await axiosPublic.get(URL, {
    params,
  });
  const book = toBookPublics(data?.data.list);
  return { ...data, data: { ...data.data, list: book } };
}

// ----------------------------------------------------------------------

export function useGetFields(params?: IParamBasic) {
  const URL = params
    ? [
      endpoints.public.field,
      {
        params: {
          ...params,
        },
      },
    ]
    : endpoints.public.field;

  const { data, isLoading, error, isValidating, mutate } = useSWR<IResponse<IField[]>>(
    URL,
    fetcherPublic
  );

  // console.log(data, isLoading, error, isValidating);

  const fields = toFields(data?.data.list);
  const memoizedValue = useMemo(
    () => ({
      data: {
        status: data?.status,
        message: data?.message,
        data: {
          list: fields || [],
          pagination: data?.data.pagination,
        },
      },
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, isLoading, error, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetBookCategories(params?: IParamBasic) {
  const URL = params
    ? [
      endpoints.public.category,
      {
        params: {
          ...params,
        },
      },
    ]
    : endpoints.public.category;

  const { data, isLoading, error, isValidating, mutate } = useSWR<IResponse<IBookShelf[]>>(
    URL,
    fetcherPublic
  );

  // console.log(data, isLoading, error, isValidating);

  const bookCategories = toBookCategories(data?.data.list);
  const memoizedValue = useMemo(
    () => ({
      data: {
        status: data?.status,
        message: data?.message,
        data: {
          list: bookCategories || [],
          pagination: data?.data.pagination,
        },
      },
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, isLoading, error, isValidating, mutate]
  );

  return memoizedValue;
}

export function useGetLanguages(params?: IParamBasic) {
  const URL = params
    ? [
      endpoints.public.language,
      {
        params: {
          ...params,
        },
      },
    ]
    : endpoints.public.language;

  const { data, isLoading, error, isValidating, mutate } = useSWR<IResponse<ILanguage[]>>(
    URL,
    fetcherPublic
  );
  // console.log(data, isLoading, error, isValidating);

  const fields = toLanguages(data?.data.list);
  const memoizedValue = useMemo(
    () => ({
      data: {
        status: data?.status,
        message: data?.message,
        data: {
          list: fields || [],
          pagination: data?.data.pagination,
        },
      },
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, isLoading, error, isValidating, mutate]
  );

  return memoizedValue;
}
export function useGetBookPublicBySlug(slug: string) {
  const URL = `${endpoints.public.find_book}/${slug}`;

  const { data, isLoading, error, isValidating, mutate } = useSWR<
    IResponseSingle<IBookDetailPublic>
  >(URL, fetcherPublic);

  const bookDetail = toBookPublic(data?.data);
  const memoizedValue = useMemo(
    () => ({
      data: {
        status: data?.status,
        message: data?.message,
        data: {
          ...bookDetail,
        },
      },
      isLoading,
      error,
      isValidating,
      mutate,
    }),
    [data, error, isLoading, isValidating, mutate]
  );

  return memoizedValue;
}

export async function findBookByAI(
  key: string,
  data: Record<string, string>,
  signal?: AbortSignal,
  modelAI: string = "mistral"
) {
  const knowledge = Object.entries(data)
    .map(([name, author]) => `- ${name} (tác giả: ${author})`)
    .join('\n');

  const prompt = `Bạn có danh sách tên sách và tác giả như sau:\n${knowledge}\nTrả lời mỗi tên sách của câu hỏi dựa theo danh sách này: "${key}"`.trim();

  // grok
  // if (GROK_FLAG) {
  //   modelAI = "grok";
  // }

  // gemini
  if (GEMINI_FLAG) {
    modelAI = "gemini";
  }

  // if (modelAI === "grok") {
  //   const client = new OpenAI({
  //     apiKey: GROK_API_KEY,
  //     baseURL: AI_GROK_HOST,
  //   });

  //   const completion = await client.chat.completions.create({
  //     model: "grok-3-latest",
  //     messages: [
  //       {
  //         role: "system",
  //         content: prompt
  //       }
  //       // {
  //       //     role: "user",
  //       //     content: "What is the meaning of life, the universe, and everything?"
  //       // },
  //     ],
  //   });
  //   return completion
  // }

  if (modelAI === "gemini") {
    const URL = AI_GEMINI_HOST;

    const bodyPayload = {
      contents: [
        {
          parts: [{ text: prompt }]
        }
      ]
    };

    const res = await axiosAI.post(URL, bodyPayload, {
      headers: { 'Content-Type': 'application/json' },
      signal
    });

    const content = res.data?.candidates?.[0]?.content?.parts?.[0]?.text || '';
    return {
      done: true,
      response: content
    };
  }

  // ollama mistral
  const URL = endpoints.reader.findByAI;

  const bodyPayload = {
    model: "mistral:latest",
    prompt,
    stream: false
  };

  const res = await axiosAI.post(URL, bodyPayload, { signal });

  return res.data;
}


export async function findBookPublic(query: string) {
  const URL = endpoints.public.find_book;
  const params = {
    limit: 15,
    page: 1,
    keyword: query,
  };
  const res = await axiosPublic.get(URL, { params });
  return res.data;
}